/**
 * The shape `/api/staff/dashboard/cohorts` actually sends, and the few things
 * the table has to work out for itself (REQ-021 AC-004, AC-005).
 *
 * As in `metrics.ts`, nothing is defined here a second time: no formula, no
 * threshold, no unit. The three cohort measures arrive in the payload's
 * `definitions`, generated from the same records `cohorts.ts` and
 * `cohortRetention.ts` compute from, so the column headings' explanations of
 * what retention and the net paying ratio are cannot drift away from what the
 * backend actually computed.
 *
 * The one thing this file does decide is that a measure with no value is never
 * turned into a figure. `CohortMeasure.reason` is present exactly when the
 * value is unavailable, and the table renders that sentence — a cohort row that
 * cannot say why its cell is empty is the failure the whole directory refuses.
 */
import type { MetricDescription, MetricValue } from "./metrics";

export interface CohortMeasure {
  value: MetricValue;
  /** The population the figure was computed over (AC-005). */
  denominator: number;
  provisional: boolean;
  /** Present exactly when the value is unavailable. */
  reason?: string;
}

export interface RetentionMeasure extends CohortMeasure {
  /** The UTC day the roster was taken: the day after the cohort's month ended. */
  baselineDate?: string;
  observedTo?: string;
  /**
   * Always true, and carried in the payload rather than assumed here: this
   * figure follows the same accounts from the baseline day, so it is a subset of
   * a fixed population and can never exceed 100%.
   */
  perAccount: true;
}

export interface NetPayingMeasure extends CohortMeasure {
  /** The UTC day the baseline was counted on — never the cohort's own month. */
  observedFrom?: string;
  observedTo?: string;
  /**
   * Always true, and carried in the payload rather than assumed here: the
   * baseline is a count of payers, not a list of them, so the ratio compares
   * two populations that are not known to overlap.
   */
  netCountRatio: true;
}

export interface CohortRow {
  /** The signup month, `YYYY-MM`, in UTC. */
  cohort: string;
  registered: number;
  payingNow: number;
  conversion: CohortMeasure;
  retention: RetentionMeasure;
  netPaying: NetPayingMeasure;
}

export interface CohortAggregate {
  registered: number;
  payingNow: number;
  conversion: CohortMeasure;
  retention: RetentionMeasure;
  netPaying: NetPayingMeasure;
}

export interface CohortsPayload {
  definitions: MetricDescription[];
  cohorts: CohortRow[];
  aggregate: CohortAggregate;
}

export interface CohortsMeta {
  computedAt: string;
  /**
   * When the registration side was observed, or `null` where the users
   * collection could not be read at all. The distinction is load-bearing: a
   * table with no rows and a store with no answer look identical on screen
   * unless the screen is made to tell them apart.
   */
  dataAsOf: string | null;
  count: number;
  provisional: number;
  retentionUnavailable: number;
  netPayingUnavailable: number;
  undatedAccounts: number;
  futureDatedAccounts: number;
  beforeHorizonAccounts: number;
  /**
   * Accounts the transition log names that the users collection no longer
   * holds. Retention's one flattering bias — a departure erased from the roster
   * takes its own churn with it — so the screen shows it rather than keeping it
   * in the payload.
   */
  unattributedAccounts: number;
}

export const COHORT_CONVERSION_ID = "cohort_conversion";
export const COHORT_RETENTION_ID = "cohort_retention";
export const COHORT_NET_PAYING_ID = "cohort_net_paying_ratio";

export function measureDefinition(
  definitions: MetricDescription[],
  id: string,
): MetricDescription | undefined {
  return definitions.find((definition) => definition.id === id);
}

/**
 * AC-005 per row, on *any* of the three measures.
 *
 * A cohort is a small number by construction, and the three measures have three
 * different denominators: a month of two thousand signups whose paying baseline
 * was eleven people is a trustworthy conversion beside two untrustworthy
 * figures. Marking the row on any of them keeps the weakest from being read as
 * the strength of the others.
 */
export function rowProvisional(row: {
  conversion: CohortMeasure;
  retention: CohortMeasure;
  netPaying: CohortMeasure;
}): boolean {
  return (
    row.conversion.provisional ||
    row.retention.provisional ||
    row.netPaying.provisional
  );
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** "September 2026", from the `YYYY-MM` the backend buckets by. */
export function cohortLabel(cohort: string): string {
  const [year, month] = cohort.split("-").map(Number);
  const name = MONTHS[(month ?? 0) - 1];
  return name && year ? `${name} ${year}` : cohort;
}

export interface DiscardedGroup {
  key: string;
  /**
   * Absent where the payload carried no counter for this group. Today's handler
   * always sends all three, but this file is the mirror of the contract and not
   * a copy of one handler: a missing counter has to be sayable, because summing
   * it produces `NaN` and a section that renders neither a count nor "None" is
   * a completeness claim nobody made.
   */
  count?: number;
  label: string;
}

/**
 * The accounts that are in the users collection and in no row of the table.
 *
 * Named individually rather than summed, because the three are different
 * problems: a missing `createdAt` is a data defect, a future month is clock
 * skew or a fixture, and a month before the snapshot horizon is a deliberate
 * limit of the window. A single "12 accounts excluded" would need someone to
 * come and ask which.
 */
export function discardedAccounts(meta: CohortsMeta): DiscardedGroup[] {
  // A counter that is not a finite number is reported as absent rather than
  // read as a zero: "none were discarded" and "nobody counted" are the two
  // facts this whole section exists to keep apart.
  const counted = (value: number): number | undefined =>
    Number.isFinite(value) ? value : undefined;

  return [
    {
      key: "undated",
      count: counted(meta.undatedAccounts),
      label: "carry no usable creation date, so they belong to no month",
    },
    {
      key: "futureDated",
      count: counted(meta.futureDatedAccounts),
      label: "are dated in a month that has not happened yet",
    },
    {
      key: "beforeHorizon",
      count: counted(meta.beforeHorizonAccounts),
      label:
        "were created before the snapshot store's own horizon, which is where the table's window starts",
    },
  ];
}
